import { useState } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { myJobsCardStyles } from "../../styles/styles";
import CustomTooltip from "../../../../Components/CustomTooltip";
import CustomTable from "../../../../Components/CustomTable";
import SkeletonTable from "../../../../Components/Skeletons/SkeletonTable";
import { useSnackbar } from "../../../../Components/SnackBar";
import CustomAccordion from "../../../../Components/CustomAccordion";
import SkeletonAccordion from "../../../../Components/Skeletons/SkeletonAccordion";
import ProductStatusDropdown from "../JobsContent/ProductStatusDropdown";
import { getLocationContent } from "../../../../Utils/getLocationContent";
import { getCurrencySymbol } from "../../../../Utils/CountriesData/getCountriesData";
import { useTranslation } from "react-i18next";
import useLoadMoreOnScroll from "../../../../hooks/useLoadMoreOnScroll";
import useProductStatusMap from "../../../../hooks/useProductStatusMap";
import { PRODUCT_STATUS } from "../../../../Constants/productOrderStatus";

const colorMap = {
  red: {
    text: "rgba(183, 72, 74, 1)",
    bg: "rgba(183, 72, 74, 0.16)",
  },
  orange: {
    text: "rgba(255, 151, 51, 1)",
    bg: "rgba(255, 151, 51, 0.16)",
  },
  green: {
    text: "rgba(28, 160, 138, 1)",
    bg: "rgba(28, 160, 138, 0.16)",
  },
};

const MyJobsCard = (props) => {
  const {
    containerRef,
    jobsType,
    data,
    isLoading,
    handleViewButtonClick,
    sortOrder,
    // 'product' | 'service' — the active item-type tab. Pending (New Jobs) is
    // mixed, so each row carries its own `type`.
    itemType,
    onJobUpdated,
    // Infinite scroll. The sentinel has to live inside whichever element
    // actually scrolls — the accordion list's own container on mobile, and
    // CustomTable's TableContainer on desktop — so it is rendered here rather
    // than by the parent table wrappers.
    hasMore,
    loadMore,
  } = props;
  const { sentinelRef, isLoadingMore } = useLoadMoreOnScroll({
    hasMore,
    loadMore,
    isLoading,
  });
  // Store and restaurant orders render identically here — same columns, same
  // status dropdown, same delivery/pickup cell. Only the Type label differs.
  const isProduct = itemType === "product" || itemType === "restaurant";
  // The New Jobs rows carry a status id; resolve it to a name rather than
  // comparing against a hardcoded id (COMY1-283).
  const { nameById: productStatusNameById } = useProductStatusMap(itemType);
  const {
    actionButtonStyle,
    dueDateCellStyle,
    tableCellStyles,
    requestIdCellStyle,
  } = myJobsCardStyles;

  const isMobile = useMediaQuery("(max-width:767px)");
  const [expandedId, setExpandedId] = useState(null);
  const showSnackbar = useSnackbar();
  const { t } = useTranslation("page-my-jobs", { keyPrefix: "table_data" });
  const { t: tCategories } = useTranslation("categories");

  const formatDate = (date) => {
    if (!date) return "-";
    const d = new Date(date);
    return d.toLocaleDateString().replace(/\//g, ".");
  };

  const getDueDateContent = (job) => {
    if (jobsType === "completed") {
      const isCancelled = job.status === "cancelled" || job.completed_date === "Canceled" || job.completed_on === "Canceled";
      return (
        <Typography
          sx={{
            ...tableCellStyles,
            fontWeight: 500,
            color: isCancelled && "rgba(183, 72, 74, 1)",
          }}
        >
          {isCancelled
            ? t("cancelled")
            : formatDate(job.completed_on || job.completed_date)}
        </Typography>
      );
    }

    // Product pending (New Jobs) rows have no due_date — show the creation
    // date instead of crashing on the destructure below.
    if (!job.due_date) {
      return (
        <Typography sx={{ ...tableCellStyles, fontWeight: 500 }}>
          {formatDate(job.creation_date)}
        </Typography>
      );
    }

    const { type, value, color, creation_date } = job.due_date;

    const textColor = colorMap[color]?.text || colorMap.green.text;
    const bgColor = colorMap[color]?.bg || colorMap.green.bg;

    if (type === "flexible") {
      return (
        <CustomTooltip
          title={`${t("order_date")} ${formatDate(creation_date)}`}
          tooltipSxStyles={{ paddingY: "5px" }}
        >
          <Box
            sx={{
              ...dueDateCellStyle,
              color: textColor,
              backgroundColor: bgColor,
            }}
          >
            {t("flexible")}
          </Box>
        </CustomTooltip>
      );
    }

    if (type === "weekends") {
      return (
        <CustomTooltip
          title={`${t("order_date")} ${formatDate(creation_date)}`}
          tooltipSxStyles={{ paddingY: "5px" }}
        >
          <Box
            sx={{
              ...dueDateCellStyle,
              color: textColor,
              backgroundColor: bgColor,
            }}
          >
            {t("weekends")}
          </Box>
        </CustomTooltip>
      );
    }

    return (
      <Box
        sx={{
          ...dueDateCellStyle,
          color: textColor,
          backgroundColor: bgColor,
        }}
      >
        {formatDate(value)}
      </Box>
    );
  };

  const getContactIcons = (job) => (
    <Box sx={{ ml: "-8px" }}>
      <CustomTooltip
        title={t("copy_phone")}
        tooltipSxStyles={{ paddingY: "5px" }}
      >
        <IconButton
          onClick={() => {
            navigator.clipboard.writeText(job.contact?.phone || job.client_phone);
            showSnackbar(t("phone_copied_alert"), "success");
          }}
        >
          <img src="/icons/phone-outgoing-black.svg" alt="phone" />
        </IconButton>
      </CustomTooltip>
      <CustomTooltip
        title={t("open_whatsapp")}
        tooltipSxStyles={{ paddingY: "5px" }}
      >
        <IconButton
          onClick={(e) => {
            e.preventDefault();
            window.open(
              `https://api.whatsapp.com/send/?phone=${job.contact?.phone || job.client_whatsapp}&text=hello`,
              "_blank",
              "noopener,noreferrer",
            );
          }}
        >
          <img src="/icons/whatsapp-icon-black.svg" alt="whatsapp" />
        </IconButton>
      </CustomTooltip>
    </Box>
  );

  const getLogoWithNameContent = (name, logo) => (
    <Box display="flex" alignItems="center">
      <Avatar
        alt=""
        src={logo}
        sx={{
          width: 32,
          height: 32,
          backgroundColor: "rgba(212, 212, 212, 1)",
          svg: { display: "none" },
        }}
      />
      <Box ml="16px">
        <Typography sx={tableCellStyles}>{name}</Typography>
      </Box>
    </Box>
  );

  const getActionButton = (job) => (
    <Button
      sx={actionButtonStyle}
      onClick={(e) => {
        e.stopPropagation();
        handleViewButtonClick(job, jobsType);
      }}
    >
      {t("view")}
    </Button>
  );

  const columns = [
    {
      id: "request_id",
      label: t("request_id"),
      render: (job) => (
        <Box
          sx={{
            maxWidth: "140px",
          }}
        >
          <CustomTooltip
            title={job.request_id}
            tooltipSxStyles={{ paddingY: "5px" }}
            tooltipTitleSxStyles={{ wordBreak: "break-all" }}
          >
            <Typography sx={requestIdCellStyle}>{String(job.request_id).slice(0, 4)}</Typography>
          </CustomTooltip>
        </Box>
      ),
      headerStyle: { width: "140px" },
      cellStyle: { width: "140px" },
    },
    // New Jobs (pending) is mixed item types → show a "Type" column. Other
    // (single-type) lists keep the service "Pricelist" column; product tabs
    // omit it (products aren't pricelist-based).
    ...(jobsType === "pending"
      ? [
          {
            id: "type",
            label: t("type"),
            render: (job) => (
              <Typography sx={tableCellStyles}>
                {job.type === "product"
                  ? t("products")
                  : job.type === "restaurant"
                    ? t("restaurants")
                    : `${t("service_prefix")} > ${tCategories(job.pricelist)}`}
              </Typography>
            ),
          },
        ]
      : !isProduct
        ? [
            {
              id: "pricelist",
              label: t("pricelist"),
              render: (job) => (
                <Typography sx={tableCellStyles}>
                  {tCategories(job.pricelist)}
                </Typography>
              ),
            },
          ]
        : []),
    {
      id: "community",
      label: t("community"),
      render: (job) =>
        getLogoWithNameContent(job.community.name, job.community.logo_url),
    },
    ...(jobsType !== "missed"
      ? [
          {
            id: "date",
            label: jobsType === "completed" ? t("completed_on") : t("due_date"),
            render: (job) => getDueDateContent(job),
            headerRender: () => (
              <Box
                sx={{
                  userSelect: "none",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {jobsType === "completed" ? t("completed_on") : t("due_date")}
                {jobsType !== "pending" ? (
                  sortOrder === "asc" ? (
                    <KeyboardArrowDownIcon />
                  ) : (
                    <KeyboardArrowUpIcon />
                  )
                ) : null}
              </Box>
            ),
          },
        ]
      : []),
    // Product Delivery Type (Delivery / Pick Up) — Products tab, non-pending.
    ...(isProduct && jobsType !== "pending"
      ? [
          {
            id: "delivery_type",
            label: t("delivery_type"),
            render: (job) => (
              <Typography sx={tableCellStyles}>
                {job.delivery_type === "pickup" ? t("pickup") : t("delivery")}
              </Typography>
            ),
          },
        ]
      : []),
    // Editable product status dropdown — Products Active tab only.
    ...(isProduct && jobsType === "active"
      ? [
          {
            id: "status",
            label: t("status"),
            render: (job) => (
              <ProductStatusDropdown job={job} onUpdated={onJobUpdated} />
            ),
          },
        ]
      : []),
    // Status text on the New Jobs (pending) table. Product orders the supplier
    // returned for re-approval stay here as "Waiting for member approval".
    ...(jobsType === "pending"
      ? [
          {
            id: "pending_status",
            label: t("status"),
            render: (job) => (
              <Typography sx={tableCellStyles}>
                {(job.type === "product" || job.type === "restaurant") &&
                productStatusNameById[job.status_id] ===
                  PRODUCT_STATUS.WAIT_RE_APPROVE_BY_MEMBER
                  ? t("status_awaiting_member")
                  : t("status_pending")}
              </Typography>
            ),
          },
        ]
      : []),
    ...(jobsType === "completed"
      ? [
          {
            id: "revenue",
            label: t("revenue"),
            // revenue is the supplier's own share and is null when the order
            // has no revenue-share record (anything settled before the ledger
            // went live, and all restaurant orders). Show a dash rather than a
            // bare currency symbol — and never fall back to the gross.
            render: (job) => (
              <Typography sx={{ ...tableCellStyles, fontWeight: 500 }}>
                {job.revenue == null
                  ? "-"
                  : `${job.revenue_is_estimate ? "~" : ""}${getCurrencySymbol(job.currency)}${job.revenue}`}
              </Typography>
            ),
          },
        ]
      : []),
    ...(jobsType !== "completed" && !isProduct
      ? [
          {
            id: "location",
            label: t("location"),
            render: (job) => getLocationContent(job.location, t, undefined, false, undefined, jobsType === "pending" || jobsType === "missed"),
          },
        ]
      : []),
    ...(jobsType !== "missed" && jobsType !== "pending"
      ? [
          {
            id: "client_name",
            label: t("client_name"),
            render: (job) =>
              getLogoWithNameContent(job.client?.name, job.client?.image_url),
          },
        ]
      : []),
    ...(jobsType === "active"
      ? [
          {
            id: "contact",
            label: t("contact"),
            render: (job) => getContactIcons(job),
          },
        ]
      : []),
    {
      id: "actions",
      label: t("actions"),
      render: (job) => getActionButton(job),
      headerStyle: { width: "100px" },
      cellStyle: { width: "100px" },
    },
  ];

  const mobileDetails = (job) => [
    { label: t("request_id"), value: job.request_id ? String(job.request_id).slice(0, 4) : "-" },
    { label: t("pricelist"), value: tCategories(job.pricelist) || "-" },
    {
      label: t("community"),
      value: job.community?.name || "-",
    },
    ...(jobsType !== "missed"
      ? [
          {
            label: jobsType === "completed" ? t("completed_on") : t("due_date"),
            value: getDueDateContent(job),
          },
        ]
      : []),
    ...(jobsType === "completed"
      ? [
          {
            label: t("revenue"),
            value:
              job.revenue == null
                ? "-"
                : `${job.revenue_is_estimate ? "~" : ""}${getCurrencySymbol(job.currency)}${job.revenue}`,
          },
        ]
      : []),
    ...(jobsType !== "completed"
      ? [
          {
            label: t("location"),
            value: getLocationContent(job.location, t, undefined, false, undefined, jobsType === "pending" || jobsType === "missed"),
          },
        ]
      : []),
    ...(jobsType !== "missed" && jobsType !== "pending"
      ? [
          {
            label: t("client_name"),
            value: job.client?.name || "-",
          },
        ]
      : []),
    ...(jobsType === "active"
      ? [
          {
            label: t("contact"),
            value: getContactIcons(job),
          },
        ]
      : []),
  ];

  const handleChange = (memberId) => (event, isExpanded) => {
    setExpandedId(isExpanded ? memberId : null);
  };

  // const isJobTypeActivePending =
  //   jobsType === "active" || jobsType === "pending";

  const loadMoreFooter = (
    <>
      {hasMore && <Box ref={sentinelRef} sx={{ height: "1px" }} />}
      {isLoadingMore && (
        <Box sx={{ display: "flex", justifyContent: "center", py: "12px" }}>
          <CircularProgress size={22} sx={{ color: "rgba(36, 139, 112, 1)" }} />
        </Box>
      )}
    </>
  );

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {isMobile ? (
        isLoading ? (
          <SkeletonAccordion />
        ) : (
          <Box ref={containerRef} sx={{ overflow: "visible", flex: "none" }}>
            {data.map((job) => (
              <CustomAccordion
                key={job.request_id}
                imageUrl={job.community?.logo_url}
                imgUrlAlt={job.community?.name}
                expanded={expandedId === job.request_id}
                onChange={handleChange(job.request_id)}
                summaryContent={
                  <Box>
                    <Typography
                      color="rgba(62, 63, 76, 0.9)"
                      sx={{
                        fontSize: "15px",
                        fontWeight: 500,
                      }}
                    >
                      {job.community?.name} ({"#" + String(job.request_id).slice(0, 4)})
                    </Typography>
                  </Box>
                }
                details={mobileDetails(job)}
                actions={getActionButton(job)}
              />
            ))}
            {loadMoreFooter}
          </Box>
        )
      ) : isLoading ? (
        <SkeletonTable columns={columns} rowCount={5} />
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
          <CustomTable
            columns={columns}
            data={data}
            containerRef={containerRef}
            containerStyle={{ paddingRight: "0px", paddingBottom: "0px" }}
            footerSlot={loadMoreFooter}
          />
        </Box>
      )}
    </Box>
  );
};

export default MyJobsCard;
